// https://github.com/ueberdosis/tiptap/blob/develop/packages/extension-table/src/table.ts

import { getRenderedAttributes, mergeAttributes, type NodeViewRendererProps } from '@tiptap/core';
import { Table as TiptapTable, TableOptions, TableView } from '@tiptap/extension-table';
import { cellAround, columnResizingPluginKey, columnResizing, ColumnResizingOptions } from '@tiptap/pm/tables';
import { TextSelection } from '@tiptap/pm/state';
import { GapCursor } from '@tiptap/pm/gapcursor';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Editor } from '@tiptap/react';
import { ELEMENT_ROW_NODE_NAME } from '../elementRow/constants';
import { Dispatch, SetStateAction } from 'react';
import createRightClickMenuPlugin from '../../plugins/createRightClickMenuPlugin';
import { NORMALIZED_BORDER_STYLES, normalizeBorderStyle, TNormalizedBorderStyle } from './normalizeBorderStyle';

export type TBorderStyle = TNormalizedBorderStyle;
export type TBorderWeight = '0.5pt' | '1pt' | '2pt' | '3pt';
export const BORDER_STYLES: TBorderStyle[] = [...NORMALIZED_BORDER_STYLES];
export const BORDER_WEIGHTS: TBorderWeight[] = ['0.5pt', '1pt', '2pt', '3pt'];
export const DEFAULT_BORDER_STYLE: TBorderStyle = 'solid';
export const DEFAULT_BORDER_WEIGHT: TBorderWeight = '0.5pt';

// Border commands live in borderCommands.ts (not as TipTap extension commands)
// because they need TableMap-based cell clearing and neighbor sync.

interface ExtendedTableOptions extends TableOptions {
  setIsTableMenuOpen?: Dispatch<SetStateAction<boolean>>;
}

type TStorage = {
  lastInsideRow: boolean | undefined;
  /** Last cell color picked in this editor; the cell color button applies it on click, as in Word. */
  lastCellColor: string | undefined;
};

declare module '@tiptap/core' {
  interface Storage {
    table: TStorage;
  }
}

type TTableAttrs = { borderStyle: TBorderStyle | null; borderWeight: TBorderWeight | null };
type TTableHTMLAttributes = Record<string, any>;
type TGetTableHTMLAttributes = (node: PMNode) => TTableHTMLAttributes;

// see https://github.com/ProseMirror/prosemirror-tables/blob/582b4e45b70da49472eed91698e5d3ecfbfcf5eb/src/tableview.ts#L8
class ExtendedTableView extends TableView {
  private editor: NodeViewRendererProps['editor'];
  private getPos: NodeViewRendererProps['getPos'];
  private topZone: HTMLDivElement;
  private bottomZone: HTMLDivElement;
  private onTopMouseDown: (event: MouseEvent) => void;
  private onBottomMouseDown: (event: MouseEvent) => void;
  private HTMLAttributes: TTableHTMLAttributes;
  private getHTMLAttributes: TGetTableHTMLAttributes;

  constructor(
    node: PMNode,
    cellMinWidth: number,
    view: NodeViewRendererProps['view'],
    HTMLAttributes: Record<string, any>,
    editor: NodeViewRendererProps['editor'],
    getPos: NodeViewRendererProps['getPos'],
    getHTMLAttributes: TGetTableHTMLAttributes
  ) {
    super(node, cellMinWidth, view, HTMLAttributes);
    this.editor = editor;
    this.getPos = getPos;
    this.HTMLAttributes = HTMLAttributes;
    this.getHTMLAttributes = getHTMLAttributes;
    syncTableBorderAttrs(this.table, node.attrs as TTableAttrs);

    this.topZone = createClickZone('top');
    this.bottomZone = createClickZone('bottom');

    this.onTopMouseDown = this.handleZoneMouseDown(true);
    this.onBottomMouseDown = this.handleZoneMouseDown(false);

    this.topZone.addEventListener('mousedown', this.onTopMouseDown);
    this.bottomZone.addEventListener('mousedown', this.onBottomMouseDown);

    this.dom.insertBefore(this.topZone, this.table);
    this.dom.appendChild(this.bottomZone);
  }

  private handleZoneMouseDown = (before: boolean) => (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const pos = this.getPos();
    if (pos === undefined) return;
    const node = this.editor.state.doc.nodeAt(pos);
    if (!node) return;
    const targetPos = before ? pos : pos + node.nodeSize;
    const { state, view } = this.editor;
    view.dispatch(state.tr.setSelection(new GapCursor(state.doc.resolve(targetPos))).scrollIntoView());
    view.focus();
  };

  update(node: PMNode) {
    if (node.type !== this.node.type) return false;

    const HTMLAttributes = this.getHTMLAttributes(node);
    syncTableHTMLAttributes(this.table, this.HTMLAttributes, HTMLAttributes);
    this.HTMLAttributes = HTMLAttributes;

    const updated = super.update(node);
    if (updated) {
      syncTableBorderAttrs(this.table, node.attrs as TTableAttrs);
      clearStaleColumnWidths(node, this.colgroup);
    }
    return updated;
  }

  destroy() {
    this.topZone.removeEventListener('mousedown', this.onTopMouseDown);
    this.bottomZone.removeEventListener('mousedown', this.onBottomMouseDown);
  }
}

/**
 * Drop a column width the table no longer has.
 *
 * A view is reused for whatever table lands in its place, and the library's column update only ever
 * ADDS a rule - a column with no width gets a `min-width` and keeps the previous table's `width`.
 */
const clearStaleColumnWidths = (node: PMNode, colgroup: HTMLTableColElement) => {
  const row = node.firstChild;
  if (!row) return;
  let index = 0;
  for (let cell = 0; cell < row.childCount; cell++) {
    const { colspan, colwidth } = row.child(cell).attrs as { colspan: number; colwidth: number[] | null };
    for (let span = 0; span < (colspan || 1); span++, index++) {
      if (colwidth?.[span]) continue;
      const col = colgroup.children[index];
      if (col instanceof HTMLElement && col.style.width) col.style.removeProperty('width');
    }
  }
};

const createClickZone = (variant: 'top' | 'bottom') => {
  const zone = document.createElement('div');
  zone.className = `table-click-zone table-click-zone--${variant}`;
  zone.contentEditable = 'false';
  zone.setAttribute('aria-hidden', 'true');
  return zone;
};

const syncTableHTMLAttributes = (
  table: HTMLTableElement,
  previousAttributes: TTableHTMLAttributes,
  nextAttributes: TTableHTMLAttributes
) => {
  const attributeNames = new Set([...Object.keys(previousAttributes), ...Object.keys(nextAttributes)]);

  attributeNames.forEach((attributeName) => {
    const previousValue = previousAttributes[attributeName];
    const nextValue = nextAttributes[attributeName];
    if (previousValue === nextValue) return;

    if (nextValue === undefined || nextValue === null) {
      table.removeAttribute(attributeName);
    } else if (attributeName === 'style') {
      table.style.cssText = String(nextValue);
    } else {
      table.setAttribute(attributeName, String(nextValue));
    }
  });
};

const syncTableBorderAttrs = (table: HTMLTableElement, attrs: TTableAttrs) => {
  syncAttr(table, 'data-border-style', attrs.borderStyle, DEFAULT_BORDER_STYLE);
  syncAttr(table, 'data-border-weight', attrs.borderWeight, DEFAULT_BORDER_WEIGHT);
};

/** Only render the data attribute when it differs from the default. */
const syncAttr = (el: HTMLElement, attrName: string, value: string | null, defaultValue: string) => {
  if (value && value !== defaultValue) {
    el.setAttribute(attrName, value);
  } else {
    el.removeAttribute(attrName);
  }
};

const exitTableOnArrow = (editor: Editor, side: 'before' | 'after'): boolean => {
  const isStart = side === 'before';
  const { state, view } = editor;
  const { selection } = state;
  if (!(selection instanceof TextSelection) || !selection.empty) return false;
  if (!view.endOfTextblock(isStart ? 'left' : 'right')) return false;

  const $cell = cellAround(selection.$head);
  if (!$cell) return false;

  const $head = selection.$head;
  for (let d = $cell.depth + 1; d < $head.depth; d++) {
    const boundary = isStart ? 0 : $head.node(d).childCount - 1;
    if ($head.index(d) !== boundary) return false;
  }
  if ($cell.index() !== (isStart ? 0 : $cell.parent.childCount - 1)) return false;
  if ($cell.index(-1) !== (isStart ? 0 : $cell.node(-1).childCount - 1)) return false;

  const targetPos = isStart ? $cell.before(-1) : $cell.after(-1);
  view.dispatch(state.tr.setSelection(new GapCursor(state.doc.resolve(targetPos))).scrollIntoView());
  return true;
};

const TableNode = TiptapTable.extend<ExtendedTableOptions>({
  addStorage() {
    return { lastInsideRow: undefined, lastCellColor: undefined } as TStorage;
  },

  onSelectionUpdate() {
    _maybeToggleResizing(this.editor, this.storage);
  },

  addOptions() {
    return {
      ...this.parent?.(),
      setIsTableMenuOpen: undefined, // unavailable by default, provided by DocumentContext
    } as ExtendedTableOptions;
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      borderStyle: {
        default: null,
        parseHTML: (element) => normalizeBorderStyle(element.getAttribute('data-border-style')),
        renderHTML: (attributes) => {
          if (!attributes.borderStyle) return {};
          return { 'data-border-style': attributes.borderStyle };
        },
      },
      borderWeight: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute('data-border-weight');
          if (value && BORDER_WEIGHTS.includes(value as TBorderWeight)) return value;
          return null;
        },
        renderHTML: (attributes) => {
          if (!attributes.borderWeight) return {};
          return { 'data-border-weight': attributes.borderWeight };
        },
      },
    };
  },

  addNodeView() {
    return (props) => {
      const extensionAttributes = props.editor.extensionManager.attributes;
      const getHTMLAttributes: TGetTableHTMLAttributes = (node) =>
        mergeAttributes(this.options.HTMLAttributes, getRenderedAttributes(node, extensionAttributes));
      const HTMLAttributes = getHTMLAttributes(props.node);
      return new ExtendedTableView(
        props.node,
        this.options.cellMinWidth,
        props.view,
        HTMLAttributes,
        props.editor,
        props.getPos,
        getHTMLAttributes
      );
    };
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() || []),
      createRightClickMenuPlugin({
        kind: 'table',
        onOpen: () => {
          this.options.setIsTableMenuOpen?.(true);
        },
        onClose: () => {
          this.options.setIsTableMenuOpen?.(false);
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      ...(this.parent?.() || {}),

      // Normalizes prosemirror-tables/gapcursor's inconsistent edge-exit behavior across containers.
      ArrowRight: () => exitTableOnArrow(this.editor, 'after'),
      ArrowLeft: () => exitTableOnArrow(this.editor, 'before'),
    };
  },
});

export default TableNode;

const _maybeToggleResizing = (editor: Editor, storage: TStorage) => {
  const { state } = editor;
  const { schema, selection } = state;
  const $from = selection.$from;

  // Only react when selection is inside a TABLE
  let inTable = false;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type === schema.nodes.table) {
      inTable = true;
      break;
    }
  }
  if (!inTable) return;

  // Compute whether that table is inside an element-row-node
  let insideRow = false;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type === schema.nodes[ELEMENT_ROW_NODE_NAME]) {
      insideRow = true;
      break;
    }
  }

  // Only toggle when the desired config changed
  if (storage.lastInsideRow === insideRow) return;
  storage.lastInsideRow = insideRow;

  toggleTableResizing(editor, true, { lastColumnResizable: insideRow });
};

/*
https://github.com/ueberdosis/tiptap/issues/4872
https://github.com/ueberdosis/tiptap/blob/89d5713a62d05611a76d7c5916784885fb1196b0/packages/extension-table/src/table.ts#L431
https://github.com/ProseMirror/prosemirror-tables/blob/master/src/columnresizing.ts
*/

export const toggleTableResizing = (editor: Editor, isEdit: boolean, options: ColumnResizingOptions = {}) => {
  // Only touch the plugin when needed; external guard will limit calls
  editor.unregisterPlugin(columnResizingPluginKey);
  if (isEdit) {
    editor.registerPlugin(
      columnResizing({
        handleWidth: 5,
        cellMinWidth: 25,
        View: TableView,
        lastColumnResizable: false,
        ...options,
      })
    );
  }
};
